import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { parseUTC } from '../utils/dateUtils';
import {
  Container, Box, Typography, Button, Card, CardHeader, CardContent,
  Grid, Chip, CircularProgress, Alert, Snackbar, TextField,
  IconButton, Tooltip, Divider, Breadcrumbs, Link, Pagination,
  Table, TableBody, TableRow, TableCell, TableHead, ToggleButton, ToggleButtonGroup,
  Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemIcon, ListItemText, Avatar,
  FormControl, InputLabel, Select, MenuItem,
  Tabs, Tab
} from '@mui/material';
import {
  ArrowBack, Public, Refresh, Sync, Launch, Save, Edit,
  CheckCircle, Error, Lock, LockOpen, People, PowerSettingsNew,
  TableChart, Language, Extension, Email, CheckCircleOutline, ErrorOutline, Shield, Warning
} from '@mui/icons-material';
import api from '../services/api';
import BackupList from '../components/BackupList';
import RestoreConfirmDialog from '../components/RestoreConfirmDialog';
import RestoreProgressPanel from '../components/RestoreProgressPanel';
import HostInfoPanel from '../components/HostInfoPanel';

const DomainDetail = () => {
  const { domainId } = useParams();
  const navigate = useNavigate();
  const [domain, setDomain] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [updatingPlugin, setUpdatingPlugin] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [latestPluginVersion, setLatestPluginVersion] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [editFields, setEditFields] = useState({
    notes: '', hosting_expiry_date: '', cloudflare_api_token: '', host_provider: ''
  });
  const [wpUsers, setWpUsers] = useState(null);
  const [wpPlugins, setWpPlugins] = useState(null);
  const [loadingWpUsers, setLoadingWpUsers] = useState(false);
  const [loadingWpPlugins, setLoadingWpPlugins] = useState(false);
  const [emailLogs, setEmailLogs] = useState(null);
  const [loadingEmailLogs, setLoadingEmailLogs] = useState(false);
  const [wpHealth, setWpHealth] = useState(null);
  const [loadingWpHealth, setLoadingWpHealth] = useState(false);
  const [mainwpData, setMainwpData] = useState(null);
  const [loadingMainwp, setLoadingMainwp] = useState(false);
  const [uptimeData, setUptimeData] = useState(null);
  const [loadingUptime, setLoadingUptime] = useState(false);
  const [emailPage, setEmailPage] = useState(1);
  const [incidentPage, setIncidentPage] = useState(1);
  const [emailFilter, setEmailFilter] = useState('all');
  const [emailDateFrom, setEmailDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [emailDateTo, setEmailDateTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [activeTab, setActiveTab] = useState(0);
  const [backups, setBackups] = useState(null);
  const [restoreDialog, setRestoreDialog] = useState({ open: false, backup: null });
  const [restoreStatus, setRestoreStatus] = useState({ in_progress: false, job: null });
  const [hostInfo, setHostInfo] = useState(null);

  useEffect(() => {
    fetchDomain();
    api.get('/admin/plugin/version').then(res => {
      setLatestPluginVersion(res.data.version || null);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId]);

  useEffect(() => {
    if (activeTab !== 1 || !domain) return;
    api.get(`/sites/${domain.site_id}/domains/${domain.id}/backups`)
      .then((res) => setBackups(res.data))
      .catch(() => setBackups([]));
  }, [activeTab, domain?.id, restoreStatus.job?.status]);

  useEffect(() => {
    if (activeTab !== 2 || !domain) return;
    let cancelled = false;
    const fetchInfo = () => {
      api.get(`/sites/${domain.site_id}/domains/${domain.id}/host-info`)
        .then((res) => { if (!cancelled) setHostInfo(res.data); })
        .catch(() => { if (!cancelled) setHostInfo(null); });
    };
    fetchInfo();
    const timer = setInterval(fetchInfo, 60000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [activeTab, domain?.id]);

  useEffect(() => {
    if (!domain) return;
    let timer = null;
    const fetchStatus = () => {
      api.get(`/sites/${domain.site_id}/domains/${domain.id}/restore-status`)
        .then((res) => setRestoreStatus(res.data))
        .catch(() => {});
    };
    fetchStatus();
    timer = setInterval(() => {
      if (restoreStatus.in_progress) fetchStatus();
    }, 5000);
    return () => clearInterval(timer);
  }, [domain?.id, restoreStatus.in_progress]);

  const handleRunBackupNow = async () => {
    try {
      await api.post(`/sites/${domain.site_id}/domains/${domain.id}/backups`);
      setTimeout(() => {
        api.get(`/sites/${domain.site_id}/domains/${domain.id}/backups`)
          .then((res) => setBackups(res.data));
      }, 2000);
    } catch (e) {
      alert('Failed to enqueue backup: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleDownloadBackup = async (backup, part) => {
    try {
      const res = await api.get(
        `/sites/${domain.site_id}/domains/${domain.id}/backups/${backup.id}/download-url?part=${part}`
      );
      const { url, filename } = res.data;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.rel = 'noopener';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      alert('Failed to download backup: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleRestoreConfirmed = async (backup) => {
    setRestoreDialog({ open: false, backup: null });
    try {
      await api.post(`/sites/${domain.site_id}/domains/${domain.id}/restore`, { backup_id: backup.id });
      setRestoreStatus({ in_progress: true, job: { id: 0, kind: 'restore', domain_name: domain.domain_name, status: 'pending', result_log: '' } });
    } catch (e) {
      alert('Failed to enqueue restore: ' + (e.response?.data?.detail || e.message));
    }
  };

  const fetchDomain = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/sites/all/domains/${domainId}`);
      setDomain(response.data);
      setEditFields({
        notes: response.data.notes || '',
        hosting_expiry_date: response.data.hosting_expiry_date || '',
        cloudflare_api_token: '',
        host_provider: response.data.host_provider || ''
      });
      // Fetch MainWP monitoring data (always)
      fetchMainwpData(response.data.site_id, response.data.id);
      fetchUptime(response.data.site_id, response.data.id);

      // Fetch WP data if plugin is installed or unknown (not explicitly false)
      if (response.data.plugin_installed !== false) {
        fetchWpHealth(response.data.site_id, response.data.id);
        fetchWpUsers(response.data.site_id, response.data.id);
        fetchWpPlugins(response.data.site_id, response.data.id);
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        fetchEmailLogs(response.data.site_id, response.data.id, 1, 'all', thirtyDaysAgo.toISOString().split('T')[0], new Date().toISOString().split('T')[0]);
      }
    } catch (err) {
      setError('Failed to load domain');
    } finally {
      setLoading(false);
    }
  };

  const fetchWpUsers = async (siteId, domId) => {
    setLoadingWpUsers(true);
    try {
      const res = await api.get(`/sites/${siteId}/domains/${domId}/wp-users`);
      setWpUsers(res.data);
    } catch (err) { setWpUsers({ success: false, error: err.message, error_type: 'unknown' }); }
    finally { setLoadingWpUsers(false); }
  };

  const fetchWpPlugins = async (siteId, domId) => {
    setLoadingWpPlugins(true);
    try {
      const res = await api.get(`/sites/${siteId}/domains/${domId}/wp-plugins`);
      setWpPlugins(res.data);
    } catch (err) { setWpPlugins({ success: false, error: err.message, error_type: 'unknown' }); }
    finally { setLoadingWpPlugins(false); }
  };

  const fetchMainwpData = async (siteId, domId) => {
    setLoadingMainwp(true);
    try {
      const res = await api.get(`/sites/${siteId}/domains/${domId}/mainwp-data`);
      if (res.data.found) setMainwpData(res.data);
    } catch (err) { /* silent */ }
    finally { setLoadingMainwp(false); }
  };

  const fetchUptime = async (siteId, domId) => {
    setLoadingUptime(true);
    try {
      const res = await api.get(`/sites/${siteId}/domains/${domId}/uptime?days=7`);
      setUptimeData(res.data);
    } catch (err) { /* silent */ }
    finally { setLoadingUptime(false); }
  };

  const fetchWpHealth = async (siteId, domId) => {
    setLoadingWpHealth(true);
    try {
      const res = await api.get(`/sites/${siteId}/domains/${domId}/wp-health`);
      setWpHealth(res.data);
    } catch (err) {
      setWpHealth({ error: err.message, error_type: 'unknown' });
    } finally {
      setLoadingWpHealth(false);
    }
  };

  const fetchEmailLogs = async (siteId, domId, page = 1, status = 'all', after = '', before = '') => {
    setLoadingEmailLogs(true);
    try {
      const params = { page, per_page: 20 };
      if (status !== 'all') params.status = status;
      if (after) params.after = after;
      if (before) params.before = before;
      const res = await api.get(`/sites/${siteId}/domains/${domId}/wp-email-logs`, { params });
      if (res.data.success) setEmailLogs(res.data);
      else setEmailLogs({ success: false, error: res.data.error });
    } catch (err) {
      setEmailLogs({ success: false, error: err.message });
    } finally {
      setLoadingEmailLogs(false);
    }
  };

  const handleEmailPageChange = (newPage) => {
    setEmailPage(newPage);
    if (domain) fetchEmailLogs(domain.site_id, domain.id, newPage, emailFilter, emailDateFrom, emailDateTo);
  };

  const handleEmailFilterChange = (newFilter) => {
    setEmailFilter(newFilter);
    setEmailPage(1);
    if (domain) fetchEmailLogs(domain.site_id, domain.id, 1, newFilter, emailDateFrom, emailDateTo);
  };

  const handleEmailDateChange = (from, to) => {
    setEmailDateFrom(from);
    setEmailDateTo(to);
    setEmailPage(1);
    if (domain) fetchEmailLogs(domain.site_id, domain.id, 1, emailFilter, from, to);
  };

  const [fixingHealth, setFixingHealth] = useState(null);
  const [cfDialog, setCfDialog] = useState(false);
  const [cfToken, setCfToken] = useState('');
  const [cfFixing, setCfFixing] = useState(false);
  const [showCfInput, setShowCfInput] = useState(false);

  const handleFixHealth = async (issue) => {
    setFixingHealth(issue);
    try {
      if (issue === 'cloudflare') {
        setCfDialog(true);
        setShowCfInput(false);
        setCfToken('');
        return;
      } else if (issue === 'credentials') {
        // Try scan first (works for unconfigured plugins)
        setSnackbar({ open: true, message: 'Attempting to configure credentials...', severity: 'info' });
        try {
          await api.post(`/sites/${domain.site_id}/domains/${domain.id}/scan`);
          // Re-check health to see if it worked
          const healthRes = await api.get(`/sites/${domain.site_id}/domains/${domain.id}/wp-health`);
          if (healthRes.data.status === 'connected') {
            setSnackbar({ open: true, message: 'Credentials configured successfully!', severity: 'success' });
            setWpHealth(healthRes.data);
            fetchDomain();
            return;
          }
        } catch (err) { /* continue to manual fix message */ }
        // If still not fixed, it's a credentials mismatch — needs manual fix
        setSnackbar({
          open: true,
          message: `Credentials mismatch on ${domain.domain_name}. The plugin was configured with different credentials. Reset via WP Admin > Settings or re-install the plugin.`,
          severity: 'warning'
        });
        fetchWpHealth(domain.site_id, domain.id);
        fetchDomain();
        return;
      } else if (issue === 'plugin') {
        // Re-scan to detect plugin
        setSnackbar({ open: true, message: 'Scanning for plugin...', severity: 'info' });
        await api.post(`/sites/${domain.site_id}/domains/${domain.id}/scan`);
        setSnackbar({ open: true, message: 'Scan complete.', severity: 'success' });
        fetchWpHealth(domain.site_id, domain.id);
        fetchDomain();
      }
    } catch (err) {
      setSnackbar({ open: true, message: `Fix failed: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    } finally {
      setFixingHealth(null);
    }
  };

  const handleScan = async () => {
    setScanning(true);
    setSnackbar({ open: true, message: `Scanning ${domain.domain_name}...`, severity: 'info' });
    try {
      const response = await api.post(`/sites/${domain.site_id}/domains/${domain.id}/scan`);
      setDomain(prev => ({ ...prev, ...response.data }));
      const msg = response.data.plugin_auto_configured
        ? `Scan complete — plugin found and configured on ${domain.domain_name}`
        : `Scan complete for ${domain.domain_name}`;
      setSnackbar({ open: true, message: msg, severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: `Scan failed: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    } finally {
      setScanning(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { ...editFields };
      if (!payload.cloudflare_api_token) delete payload.cloudflare_api_token;
      if (payload.host_provider === '') payload.host_provider = null;
      await api.put(`/sites/${domain.site_id}/domains/${domain.id}`, payload);
      setSnackbar({ open: true, message: 'Domain updated', severity: 'success' });
      fetchDomain();
    } catch (err) {
      setSnackbar({ open: true, message: `Save failed: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePlugin = async () => {
    setUpdatingPlugin(true);
    setSnackbar({ open: true, message: `Updating plugin on ${domain.domain_name}...`, severity: 'info' });
    try {
      const response = await api.post(`/admin/plugin/update-domain/${domain.id}`);
      if (response.data.success) {
        setDomain(prev => ({ ...prev, plugin_version: response.data.new_version }));
        setSnackbar({
          open: true,
          message: `Plugin updated: ${response.data.previous_version} → ${response.data.new_version}`,
          severity: 'success'
        });
      } else {
        setSnackbar({ open: true, message: `Update failed: ${response.data.error}`, severity: 'error' });
      }
    } catch (err) {
      setSnackbar({ open: true, message: `Update failed: ${err.response?.data?.detail || err.message}`, severity: 'error' });
    } finally {
      setUpdatingPlugin(false);
    }
  };

  const handleWordPressLogin = async () => {
    const siteUrl = domain.final_url || `https://${domain.domain_name}`;
    setLoggingIn(true);
    try {
      const response = await api.post('/auth/wordpress/sync', {
        site_url: siteUrl,
        email: '',
        display_name: ''
      });
      if (response.data.success && response.data.wordpress_response?.dashboard_url) {
        setSnackbar({ open: true, message: 'WordPress login successful! Redirecting...', severity: 'success' });
        setTimeout(() => {
          window.open(response.data.wordpress_response.dashboard_url, '_blank');
        }, 1000);
      } else {
        setSnackbar({ open: true, message: 'WP Login failed — no dashboard URL returned', severity: 'error' });
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'WP Login failed';
      setSnackbar({ open: true, message: detail, severity: 'error' });
    } finally {
      setLoggingIn(false);
    }
  };

  const isPluginOutdated = () => {
    if (!latestPluginVersion || !domain?.plugin_version) return true;
    const normalize = (v) => (v || '').replace(/^v/i, '').trim();
    return normalize(domain.plugin_version) !== normalize(latestPluginVersion);
  };

  const StatusChip = ({ label, value, color, icon }) => (
    <Chip
      icon={icon}
      label={`${label}: ${value}`}
      size="small"
      color={color}
      variant="outlined"
      sx={{ fontWeight: 500, fontSize: '0.75rem' }}
    />
  );

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={36} sx={{ color: 'primary.main' }} />
      </Container>
    );
  }

  if (error || !domain) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error || 'Domain not found'}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/domains')} sx={{ mt: 2 }}>
          Back to Domains
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          underline="hover"
          color="text.secondary"
          sx={{ cursor: 'pointer', fontSize: '0.875rem' }}
          onClick={() => navigate('/domains')}
        >
          Domains
        </Link>
        {domain.account_name && (
          <Link
            underline="hover"
            color="text.secondary"
            sx={{ cursor: 'pointer', fontSize: '0.875rem' }}
            onClick={() => navigate(`/sites/${domain.site_id}/edit`)}
          >
            {domain.account_name}
          </Link>
        )}
        <Typography color="text.primary" sx={{ fontSize: '0.875rem', fontWeight: 500 }}>
          {domain.domain_name}
        </Typography>
      </Breadcrumbs>

      {/* Header (visible on every tab) */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: 2,
              bgcolor: domain.is_active ? 'success.main' : 'grey.400',
              display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2,
              boxShadow: '0 4px 12px rgba(23, 75, 106, 0.25)'
            }}>
              <Public sx={{ fontSize: 28, color: 'white' }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 500, letterSpacing: '-0.01em'
              }}>
                {domain.domain_name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                <StatusChip
                  label="Status"
                  value={domain.is_active === true ? 'Active' : domain.is_active === false ? 'Down' : 'Unknown'}
                  color={domain.is_active === true ? 'success' : domain.is_active === false ? 'error' : 'default'}
                />
                {domain.ssl_status && (
                  <StatusChip
                    label="SSL"
                    value={domain.ssl_status}
                    color={domain.ssl_status === 'valid' ? 'success' : domain.ssl_status === 'expired' ? 'error' : 'warning'}
                    icon={domain.ssl_status === 'valid' ? <Lock sx={{ fontSize: 14 }} /> : <LockOpen sx={{ fontSize: 14 }} />}
                  />
                )}
                {domain.plugin_installed && (
                  <StatusChip
                    label="Plugin"
                    value={domain.plugin_version || 'Installed'}
                    color="success"
                    icon={<Extension sx={{ fontSize: 14 }} />}
                  />
                )}
                {domain.is_primary && (
                  <Chip label="Primary" size="small" color="primary" sx={{ fontWeight: 600, fontSize: '0.75rem' }} />
                )}
                {domain.mainwp_id ? (
                  <StatusChip label="MainWP" value="Connected" color="success" />
                ) : (
                  <StatusChip label="MainWP" value="Not Connected" color="default" />
                )}
              </Box>
            </Box>
          </Box>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexShrink: 0, flexWrap: 'nowrap' }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={scanning ? <CircularProgress size={14} /> : <Refresh />}
              onClick={handleScan}
              disabled={scanning}
              sx={{
                borderColor: 'grey.300', color: 'text.primary',
                textTransform: 'none', fontWeight: 500, px: 2,
                '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(23, 75, 106, 0.04)' }
              }}
            >
              {scanning ? 'Scanning...' : 'Scan'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<Edit />}
              onClick={() => navigate(`/sites/${domain.site_id}/edit?tab=domains`)}
              sx={{
                borderColor: 'grey.300', color: 'text.primary',
                textTransform: 'none', fontWeight: 500, px: 2,
                '&:hover': { borderColor: 'primary.main', bgcolor: 'rgba(23, 75, 106, 0.04)' }
              }}
            >
              Edit
            </Button>
            {domain.plugin_installed && (
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={loggingIn ? <CircularProgress size={14} color="inherit" /> : <Sync />}
                onClick={handleWordPressLogin}
                disabled={loggingIn}
                sx={{ textTransform: 'none', fontWeight: 500, px: 2, boxShadow: 'none',
                  '&:hover': { boxShadow: '0 2px 8px rgba(46,125,50,0.25)' }
                }}
              >
                WP Login
              </Button>
            )}
            {domain.plugin_installed && isPluginOutdated() && (
              <Button
                size="small"
                variant="outlined"
                color={updatingPlugin ? 'warning' : 'primary'}
                startIcon={updatingPlugin ? <CircularProgress size={14} /> : <Extension />}
                onClick={handleUpdatePlugin}
                disabled={updatingPlugin}
                sx={{ textTransform: 'none', fontWeight: 500, px: 2 }}
              >
                {updatingPlugin ? 'Updating...' : `Update to ${latestPluginVersion}`}
              </Button>
            )}
            {domain.final_url && (
              <Tooltip title="Visit site" arrow>
                <IconButton
                  size="small"
                  onClick={() => window.open(domain.final_url, '_blank')}
                  sx={{ color: 'grey.500', '&:hover': { color: 'primary.main' } }}
                >
                  <Launch fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </Box>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab label="Overview" />
        <Tab label="Backups" />
        <Tab label="Host info" />
      </Tabs>

      {activeTab === 0 && (
      <Box>
      {/* Connection Error Banner */}
      {(() => {
        if (!wpHealth || wpHealth.status === 'connected') return null;

        const statusConfig = {
          cloudflare: { title: 'Cloudflare is blocking Nerv', severity: 'warning', action: 'cloudflare' },
          unconfigured: { title: 'Plugin credentials not configured', severity: 'warning', action: 'credentials' },
          auth_failed: { title: 'Plugin authentication failed', severity: 'error', action: 'credentials' },
          no_plugin: { title: 'NKP Control Panel plugin not found', severity: 'error', action: 'plugin' },
          unreachable: { title: 'Cannot connect to the website', severity: 'error', action: 'scan' },
          timeout: { title: 'Website timed out', severity: 'error', action: 'scan' },
        };
        const cfg = statusConfig[wpHealth.status] || { title: 'Connection issue', severity: 'error', action: 'scan' };

        return (
          <Alert
            severity={cfg.severity}
            variant="filled"
            sx={{ mb: 3, borderRadius: 2, '& .MuiAlert-message': { width: '100%' } }}
            action={
              <Button color="inherit" size="small" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}
                onClick={() => {
                  if (cfg.action === 'cloudflare') handleFixHealth('cloudflare');
                  else if (cfg.action === 'credentials') handleFixHealth('credentials');
                  else if (cfg.action === 'plugin') handleFixHealth('plugin');
                  else handleScan();
                }}
                disabled={!!fixingHealth || scanning}
              >
                {fixingHealth || scanning ? 'Fixing...' : 'Fix'}
              </Button>
            }
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {cfg.title}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              {wpHealth.message}
            </Typography>
          </Alert>
        );
      })()}

      <Grid container spacing={3}>
        {/* Overview */}
        <Grid item xs={12} md={8}>
          <Card sx={{
            borderRadius: 3, border: '1px solid', borderColor: 'grey.200',
            boxShadow: '0px 12px 18px -6px rgba(0, 0, 0, 0.08)'
          }}>
            <CardHeader
              title="Overview"
              avatar={<Language color="primary" />}
              titleTypographyProps={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 500, fontSize: '1.3rem'
              }}
            />
            <CardContent>
              <Grid container spacing={2}>
                {[
                  { label: 'Parent Site', value: domain.account_name || '—', link: `/sites/${domain.site_id}/edit` },
                  { label: 'Final URL', value: domain.final_url || '—' },
                  { label: 'IP Address', value: domain.ip_address || '—' },
                  { label: 'CMS', value: domain.cms_detected || '—' },
                  { label: 'Hosting', value: domain.host_provider || domain.hosting_provider || '—' },
                  { label: 'DNS Provider', value: domain.dns_provider || '—' },
                  { label: 'Registrar', value: domain.registrar || '—' },
                  { label: 'Domain Expiry', value: domain.domain_expiry_date || '—' },
                  { label: 'Cloudflare', value: domain.cloudflare_status === true ? 'Yes' : domain.cloudflare_status === false ? 'No' : '—' },
                ].map((item) => (
                  <Grid item xs={12} sm={6} key={item.label}>
                    <Typography variant="overline" sx={{
                      color: 'text.secondary', fontWeight: 600,
                      letterSpacing: '0.08em', fontSize: '0.65rem', display: 'block'
                    }}>
                      {item.label}
                    </Typography>
                    {item.link ? (
                      <Link
                        onClick={() => navigate(item.link)}
                        sx={{ cursor: 'pointer', fontWeight: 500, fontSize: '0.9rem' }}
                        underline="hover"
                      >
                        {item.value}
                      </Link>
                    ) : (
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {item.value}
                      </Typography>
                    )}
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Plugin & Scan */}
        <Grid item xs={12} md={4}>
          <Card sx={{
            borderRadius: 3, border: '1px solid', borderColor: 'grey.200',
            boxShadow: '0px 12px 18px -6px rgba(0, 0, 0, 0.08)', mb: 3
          }}>
            <CardHeader
              title="Plugin"
              avatar={<Extension color="primary" />}
              titleTypographyProps={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 500, fontSize: '1.3rem'
              }}
            />
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                {domain.plugin_installed ? (
                  <CheckCircle sx={{ color: 'success.main' }} />
                ) : (
                  <Error sx={{ color: 'grey.400' }} />
                )}
                <Typography variant="body1" fontWeight={600}>
                  {domain.plugin_installed ? 'Installed' : 'Not Installed'}
                </Typography>
              </Box>
              {domain.plugin_installed && (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    Version: <strong>{domain.plugin_version || 'Unknown'}</strong>
                    {latestPluginVersion && !isPluginOutdated() && (
                      <Chip label="Latest" size="small" color="success" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                    )}
                    {latestPluginVersion && isPluginOutdated() && (
                      <Chip label={`${latestPluginVersion} available`} size="small" color="warning" sx={{ ml: 1, height: 18, fontSize: '0.6rem' }} />
                    )}
                  </Typography>
                </>
              )}
              <Divider sx={{ my: 2, borderColor: 'grey.200' }} />
              <Typography variant="overline" sx={{
                color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', fontSize: '0.65rem'
              }}>
                Connection Health
              </Typography>
              {loadingWpHealth ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <CircularProgress size={14} /> <Typography variant="body2" color="text.secondary">Checking...</Typography>
                </Box>
              ) : wpHealth ? (
                <Box sx={{ mt: 0.5 }}>
                  <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1, p: 1, borderRadius: 1.5,
                    bgcolor: wpHealth.status === 'connected' ? 'rgba(46,125,50,0.06)' :
                             wpHealth.status === 'cloudflare' ? 'rgba(198,168,98,0.08)' :
                             'rgba(211,47,47,0.06)',
                    border: '1px solid',
                    borderColor: wpHealth.status === 'connected' ? 'rgba(46,125,50,0.2)' :
                                 wpHealth.status === 'cloudflare' ? 'rgba(198,168,98,0.3)' :
                                 'rgba(211,47,47,0.2)'
                  }}>
                    {wpHealth.status === 'connected' ? (
                      <CheckCircle sx={{ fontSize: 20, color: 'success.main' }} />
                    ) : (
                      <Error sx={{ fontSize: 20, color: wpHealth.status === 'cloudflare' ? 'warning.main' : 'error.main' }} />
                    )}
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                        {wpHealth.status === 'connected' ? 'Connected' :
                         wpHealth.status === 'cloudflare' ? 'Cloudflare Blocked' :
                         wpHealth.status === 'unconfigured' ? 'Not Configured' :
                         wpHealth.status === 'no_plugin' ? 'Plugin Not Found' :
                         wpHealth.status === 'auth_failed' ? 'Auth Failed' :
                         wpHealth.status === 'unreachable' ? 'Unreachable' :
                         wpHealth.status === 'timeout' ? 'Timed Out' : 'Error'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {wpHealth.message}
                      </Typography>
                    </Box>
                    {wpHealth.status !== 'connected' && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          if (wpHealth.status === 'cloudflare') handleFixHealth('cloudflare');
                          else if (wpHealth.status === 'unconfigured' || wpHealth.status === 'auth_failed') handleFixHealth('credentials');
                          else if (wpHealth.status === 'no_plugin') handleFixHealth('plugin');
                          else handleScan();
                        }}
                        disabled={!!fixingHealth || scanning}
                        sx={{ fontSize: '0.7rem', fontWeight: 600, textTransform: 'none', minWidth: 'auto', px: 1.5 }}
                      >
                        {fixingHealth || scanning ? 'Fixing...' : 'Fix'}
                      </Button>
                    )}
                  </Box>
                </Box>
              ) : null}
              <Divider sx={{ my: 2, borderColor: 'grey.200' }} />
              <Typography variant="overline" sx={{
                color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', fontSize: '0.65rem'
              }}>
                Scan Info
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Last: {domain.last_scanned ? parseUTC(domain.last_scanned).toLocaleString() : 'Never'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Status: {domain.scan_status || '—'}
              </Typography>
            </CardContent>
          </Card>

          {/* Google Sheets */}
          <Card sx={{
            borderRadius: 3, border: '1px solid', borderColor: 'grey.200',
            boxShadow: '0px 12px 18px -6px rgba(0, 0, 0, 0.08)'
          }}>
            <CardHeader
              title="Google Sheets"
              avatar={<TableChart color="primary" />}
              titleTypographyProps={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 500, fontSize: '1.3rem'
              }}
            />
            <CardContent>
              {domain.google_sheet_url ? (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Launch />}
                    onClick={() => window.open(domain.google_sheet_url, '_blank')}
                    fullWidth
                    sx={{ mb: 1, textTransform: 'none' }}
                  >
                    Open Sheet
                  </Button>
                  {domain.google_sheet_last_updated && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Last updated: {parseUTC(domain.google_sheet_last_updated).toLocaleString()}
                    </Typography>
                  )}
                  {domain.google_sheet_shared_emails && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                      Shared: {(() => {
                        try {
                          const parsed = JSON.parse(domain.google_sheet_shared_emails);
                          return Array.isArray(parsed) ? parsed.join(', ') : domain.google_sheet_shared_emails;
                        } catch { return domain.google_sheet_shared_emails; }
                      })()}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No sheet linked
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* MainWP Site Intelligence */}
        {mainwpData && (
          <Grid item xs={12}>
            <Card sx={{
              borderRadius: 3, border: '1px solid', borderColor: 'grey.200',
              boxShadow: '0px 12px 18px -6px rgba(0, 0, 0, 0.08)'
            }}>
              <CardHeader
                title="Site Intelligence"
                subheader="Data from MainWP"
                avatar={<Language color="primary" />}
                titleTypographyProps={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontWeight: 500, fontSize: '1.3rem'
                }}
                subheaderTypographyProps={{ fontSize: '0.7rem' }}
                action={
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {mainwpData.updates && (mainwpData.updates.plugins_count + mainwpData.updates.themes_count) > 0 && (
                      <Chip
                        label={`${mainwpData.updates.plugins_count + mainwpData.updates.themes_count} updates available`}
                        size="small"
                        sx={{ fontWeight: 600, fontSize: '0.65rem', bgcolor: 'rgba(198,168,98,0.15)', color: 'warning.dark' }}
                      />
                    )}
                    {mainwpData.health?.score < 0 && (
                      <Chip label="Needs Attention" size="small"
                        sx={{ fontWeight: 600, fontSize: '0.65rem', bgcolor: 'rgba(211,47,47,0.1)', color: 'error.dark' }}
                      />
                    )}
                  </Box>
                }
              />
              <CardContent sx={{ pt: 0 }}>
                <Grid container spacing={3}>
                  {/* Left: WP Version, Health, DB, SEO */}
                  <Grid item xs={12} md={4}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={6}>
                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', fontSize: '0.6rem' }}>
                          WP Version
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {mainwpData.wp_version || '—'}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Tooltip arrow placement="top" title={
                          <Box sx={{ p: 0.5, maxWidth: 250 }}>
                            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>WordPress Site Health Score</Typography>
                            <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
                              Based on WordPress core diagnostics — checks PHP version, database performance, security headers, and plugin compatibility.
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', color: 'success.light' }}>0 or above = Good</Typography>
                            <Typography variant="caption" sx={{ display: 'block', color: 'warning.light' }}>-1 to -20 = Minor issues</Typography>
                            <Typography variant="caption" sx={{ display: 'block', color: 'error.light' }}>Below -20 = Needs attention</Typography>
                          </Box>
                        }>
                          <Box sx={{ cursor: 'help' }}>
                            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', fontSize: '0.6rem' }}>
                              Health Score
                            </Typography>
                            <Typography variant="body2" fontWeight={500} sx={{
                              color: mainwpData.health?.score >= 0 ? 'success.main' :
                                     mainwpData.health?.score >= -20 ? 'warning.main' : 'error.main'
                            }}>
                              {mainwpData.health?.score ?? '—'}
                              {mainwpData.health?.score != null && (
                                <Chip
                                  label={mainwpData.health.score >= 0 ? 'Good' : mainwpData.health.score >= -20 ? 'Fair' : 'Poor'}
                                  size="small"
                                  sx={{
                                    ml: 0.75, height: 16, fontSize: '0.55rem', fontWeight: 600,
                                    bgcolor: mainwpData.health.score >= 0 ? 'rgba(46,125,50,0.1)' :
                                             mainwpData.health.score >= -20 ? 'rgba(198,168,98,0.15)' : 'rgba(211,47,47,0.1)',
                                    color: mainwpData.health.score >= 0 ? 'success.dark' :
                                           mainwpData.health.score >= -20 ? 'warning.dark' : 'error.dark'
                                  }}
                                />
                              )}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', fontSize: '0.6rem' }}>
                          DB Size
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {mainwpData.health?.db_size_bytes
                            ? `${(mainwpData.health.db_size_bytes / 1024 / 1024).toFixed(1)} MB`
                            : '—'}
                        </Typography>
                      </Grid>
                      {mainwpData.seo_plugin && (
                        <Grid item xs={6}>
                          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', fontSize: '0.6rem' }}>
                            SEO Plugin
                          </Typography>
                          <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
                            {mainwpData.seo_plugin.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            v{mainwpData.seo_plugin.version}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>

                  {/* Middle: Pending Plugin Updates */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', fontSize: '0.6rem', mb: 1, display: 'block' }}>
                      Pending Plugin Updates ({mainwpData.updates?.plugins_count || 0})
                    </Typography>
                    {mainwpData.updates?.plugins_count > 0 ? (
                      <Table size="small" sx={{ '& td': { borderColor: 'grey.100', py: 0.5, px: 1 } }}>
                        <TableBody>
                          {mainwpData.updates.plugins.slice(0, 6).map((p, i) => (
                            <TableRow key={i}>
                              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{p.name}</TableCell>
                              <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                {p.current_version} → {p.new_version}
                              </TableCell>
                            </TableRow>
                          ))}
                          {mainwpData.updates.plugins_count > 6 && (
                            <TableRow>
                              <TableCell colSpan={2} sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                +{mainwpData.updates.plugins_count - 6} more
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        All plugins up to date
                      </Typography>
                    )}
                  </Grid>

                  {/* Right: Pending Theme Updates */}
                  <Grid item xs={12} md={4}>
                    <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', fontSize: '0.6rem', mb: 1, display: 'block' }}>
                      Pending Theme Updates ({mainwpData.updates?.themes_count || 0})
                    </Typography>
                    {mainwpData.updates?.themes_count > 0 ? (
                      <Table size="small" sx={{ '& td': { borderColor: 'grey.100', py: 0.5, px: 1 } }}>
                        <TableBody>
                          {mainwpData.updates.themes.map((t, i) => (
                            <TableRow key={i}>
                              <TableCell sx={{ fontSize: '0.75rem', fontWeight: 500 }}>{t.name}</TableCell>
                              <TableCell sx={{ fontSize: '0.7rem', color: 'text.secondary', whiteSpace: 'nowrap' }}>
                                {t.current_version} → {t.new_version || '?'}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                        All themes up to date
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {loadingMainwp && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>Loading monitoring data...</Typography>
            </Box>
          </Grid>
        )}

        {/* Uptime Card */}
        {loadingUptime && (
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} sx={{ color: 'primary.main' }} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>Loading uptime data...</Typography>
            </Box>
          </Grid>
        )}

        {!loadingUptime && uptimeData && uptimeData.total_checks > 0 && (() => {
          const uptimePct = uptimeData.uptime_percentage;
          const uptimeChipSx = uptimePct == null ? {} :
            uptimePct >= 99 ? { bgcolor: '#e8f5e9 !important', color: '#2e7d32 !important', fontWeight: 700 } :
            uptimePct >= 95 ? { bgcolor: '#fff8e1 !important', color: '#f57f17 !important', fontWeight: 700 } :
            { bgcolor: '#ffebee !important', color: '#c62828 !important', fontWeight: 700 };
          const recentChecks = [...(uptimeData.recent_checks || [])].reverse();
          const maxBarHeight = 40;
          const maxMs = 2000;

          return (
            <Grid item xs={12}>
              <Card sx={{
                borderRadius: 3, border: '1px solid', borderColor: 'grey.200',
                boxShadow: '0px 12px 18px -6px rgba(0, 0, 0, 0.08)'
              }}>
                <CardHeader
                  title="Uptime"
                  avatar={<CheckCircleOutline color="primary" />}
                  titleTypographyProps={{
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    fontWeight: 500, fontSize: '1.3rem'
                  }}
                  action={
                    uptimeData.active_incident ? (
                      <Chip label="DOWN" size="small" sx={{ fontWeight: 700, fontSize: '0.8rem', bgcolor: 'rgba(211,47,47,0.15)', color: 'error.dark' }} />
                    ) : uptimePct != null ? (
                      <Chip
                        label={`${uptimePct.toFixed(2)}%`}
                        size="small"
                        sx={{ fontSize: '0.8rem', ...uptimeChipSx }}
                      />
                    ) : null
                  }
                />
                <CardContent>
                  {uptimeData.active_incident && (
                    <Alert severity="error" variant="filled" sx={{ mb: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        Site is currently down — {uptimeData.active_incident.duration_minutes} minutes
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Error: {uptimeData.active_incident.error} | Checks: {uptimeData.active_incident.checks_count} | Since: {parseUTC(uptimeData.active_incident.started_at).toLocaleString()}
                      </Typography>
                    </Alert>
                  )}
                  {/* Stats Row */}
                  <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="overline" sx={{ fontSize: '0.6rem', letterSpacing: '0.08em', color: 'text.secondary', display: 'block' }}>Status</Typography>
                      <Chip
                        label={uptimeData.current_status === 'up' ? 'Online' : 'Down'}
                        color={uptimeData.current_status === 'up' ? 'success' : 'error'}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="overline" sx={{ fontSize: '0.6rem', letterSpacing: '0.08em', color: 'text.secondary', display: 'block' }}>Avg Response</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {uptimeData.avg_response_ms != null ? `${Math.round(uptimeData.avg_response_ms)}ms` : '—'}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="overline" sx={{ fontSize: '0.6rem', letterSpacing: '0.08em', color: 'text.secondary', display: 'block' }}>Checks (7d)</Typography>
                      <Typography variant="body2" fontWeight={600}>{uptimeData.total_checks}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <Typography variant="overline" sx={{ fontSize: '0.6rem', letterSpacing: '0.08em', color: 'text.secondary', display: 'block' }}>Last Check</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {uptimeData.last_check_at ? parseUTC(uptimeData.last_check_at).toLocaleString() : '—'}
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Response Time Bars */}
                  {recentChecks.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="overline" sx={{ fontSize: '0.6rem', letterSpacing: '0.08em', color: 'text.secondary', display: 'block', mb: 1 }}>Response Time (newest right)</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: `${maxBarHeight}px` }}>
                        {recentChecks.map((chk, idx) => {
                          const isDown = !chk.is_up;
                          const ms = chk.response_ms || 0;
                          const barHeight = isDown ? maxBarHeight : Math.max(3, Math.round((Math.min(ms, maxMs) / maxMs) * maxBarHeight));
                          const barColor = isDown ? '#ef5350' : ms < 500 ? '#66bb6a' : ms < 1000 ? '#ffa726' : '#ff9800';
                          const label = isDown
                            ? `DOWN — ${chk.checked_at ? parseUTC(chk.checked_at).toLocaleString() : ''}`
                            : `${ms}ms — ${chk.checked_at ? parseUTC(chk.checked_at).toLocaleString() : ''}`;
                          return (
                            <Tooltip key={idx} title={label} placement="top">
                              <Box sx={{
                                width: '4px',
                                height: `${barHeight}px`,
                                bgcolor: barColor,
                                borderRadius: '1px',
                                cursor: 'default',
                                flexShrink: 0
                              }} />
                            </Tooltip>
                          );
                        })}
                      </Box>
                    </Box>
                  )}

                </CardContent>
              </Card>
            </Grid>
          );
        })()}

        {/* Incident Report */}
        {uptimeData && uptimeData.incidents && uptimeData.incidents.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{
              borderRadius: 3, border: '1px solid', borderColor: 'grey.200',
              boxShadow: '0px 12px 18px -6px rgba(0, 0, 0, 0.08)'
            }}>
              <CardHeader
                title="Incident Report"
                avatar={<ErrorOutline color="error" />}
                titleTypographyProps={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontWeight: 500, fontSize: '1.3rem'
                }}
                action={
                  <Chip label={`${uptimeData.incidents.length} incidents`} size="small"
                    sx={{ fontWeight: 600, fontSize: '0.7rem', bgcolor: 'rgba(211,47,47,0.1)', color: 'error.dark' }} />
                }
              />
              <CardContent sx={{ pt: 0, maxHeight: 400, overflow: 'auto' }}>
                <List dense disablePadding>
                  {uptimeData.incidents
                    .slice((incidentPage - 1) * 10, incidentPage * 10)
                    .map((inc, index) => (
                    <React.Fragment key={inc.id || index}>
                      {index > 0 && <Divider sx={{ my: 0.5 }} />}
                      <ListItem sx={{ px: 1, py: 1.25, borderRadius: 1, '&:hover': { bgcolor: 'grey.50' } }}>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Avatar sx={{
                            width: 32, height: 32,
                            bgcolor: inc.ended_at || inc.resolved_at ? 'grey.200' : 'error.main',
                            fontSize: '0.75rem'
                          }}>
                            <ErrorOutline sx={{ fontSize: 18, color: inc.ended_at || inc.resolved_at ? 'text.secondary' : 'white' }} />
                          </Avatar>
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>
                              Down for {inc.duration_minutes || 0} min on {parseUTC(inc.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {parseUTC(inc.started_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {inc.error_type ? inc.error_type.replace('_', ' ') : ''}{inc.error ? `: ${inc.error}` : ''}{inc.checks_count ? ` — ${inc.checks_count} checks` : ''}
                            </Typography>
                          }
                        />
                        <Chip
                          label={inc.ended_at || inc.resolved_at ? 'Resolved' : 'Ongoing'}
                          size="small"
                          sx={{
                            height: 20, fontSize: '0.65rem', fontWeight: 600,
                            bgcolor: inc.ended_at || inc.resolved_at ? 'rgba(46,125,50,0.1)' : 'rgba(211,47,47,0.1)',
                            color: inc.ended_at || inc.resolved_at ? 'success.dark' : 'error.dark'
                          }}
                        />
                      </ListItem>
                    </React.Fragment>
                  ))}
                </List>
                {uptimeData.incidents.length > 10 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                      count={Math.ceil(uptimeData.incidents.length / 10)}
                      page={incidentPage}
                      onChange={(e, v) => setIncidentPage(v)}
                      size="small"
                      color="primary"
                    />
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Manual Fields */}
        <Grid item xs={12}>
          <Card sx={{
            borderRadius: 3, border: '1px solid', borderColor: 'grey.200',
            boxShadow: '0px 12px 18px -6px rgba(0, 0, 0, 0.08)'
          }}>
            <CardHeader
              title="Settings"
              avatar={<Edit color="primary" />}
              titleTypographyProps={{
                fontFamily: '"Cormorant Garamond", Georgia, serif',
                fontWeight: 500, fontSize: '1.3rem'
              }}
              action={
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
                  onClick={handleSave}
                  disabled={saving}
                  sx={{
                    bgcolor: 'primary.main', boxShadow: 'none',
                    '&:hover': { bgcolor: 'primary.dark', boxShadow: '0 4px 12px rgba(23, 75, 106, 0.25)' }
                  }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </Button>
              }
            />
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    value={editFields.notes}
                    onChange={(e) => setEditFields(prev => ({ ...prev, notes: e.target.value }))}
                    multiline
                    rows={3}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Hosting Expiry Date"
                    type="date"
                    value={editFields.hosting_expiry_date}
                    onChange={(e) => setEditFields(prev => ({ ...prev, hosting_expiry_date: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Cloudflare API Token"
                    value={editFields.cloudflare_api_token}
                    onChange={(e) => setEditFields(prev => ({ ...prev, cloudflare_api_token: e.target.value }))}
                    placeholder={domain.cloudflare_api_token_masked || 'Enter token'}
                    helperText={domain.cloudflare_api_token_masked ? `Current: ${domain.cloudflare_api_token_masked}` : 'No token set'}
                    type="password"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel id="host-provider-label">Host provider</InputLabel>
                    <Select
                      labelId="host-provider-label"
                      label="Host provider"
                      value={editFields.host_provider || ''}
                      onChange={(e) => setEditFields(prev => ({ ...prev, host_provider: e.target.value || '' }))}
                    >
                      <MenuItem value=""><em>(unset)</em></MenuItem>
                      <MenuItem value="nerv">nerv</MenuItem>
                      <MenuItem value="wpengine">wpengine</MenuItem>
                      <MenuItem value="siteground">siteground</MenuItem>
                      <MenuItem value="kinsta">kinsta</MenuItem>
                      <MenuItem value="cloudways">cloudways</MenuItem>
                      <MenuItem value="other">other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* WordPress Users */}
        {domain.plugin_installed !== false && (
          <Grid item xs={12} md={6}>
            <Card sx={{
              borderRadius: 3, border: '1px solid', borderColor: 'grey.200',
              boxShadow: '0px 12px 18px -6px rgba(0, 0, 0, 0.08)',
              height: 400, display: 'flex', flexDirection: 'column'
            }}>
              <CardHeader
                title="WordPress Users"
                avatar={<People color="primary" />}
                titleTypographyProps={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontWeight: 500, fontSize: '1.3rem'
                }}
                action={
                  wpUsers && (
                    <Chip label={`${wpUsers.total} users`} size="small"
                      sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', fontSize: '0.7rem' }}
                    />
                  )
                }
              />
              <CardContent sx={{ pt: 0, flex: 1, overflow: 'auto' }}>
                {loadingWpUsers ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} sx={{ color: 'primary.main' }} />
                  </Box>
                ) : wpUsers?.success && wpUsers?.users ? (
                  <Table size="small" sx={{ '& td, & th': { borderColor: 'grey.100' } }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1, borderBottom: '2px solid', borderColor: 'grey.200' }}>User</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1, borderBottom: '2px solid', borderColor: 'grey.200' }}>Role</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1, borderBottom: '2px solid', borderColor: 'grey.200' }} align="right">Posts</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {wpUsers.users.map((user) => (
                        <TableRow key={user.id} sx={{ '&:hover': { bgcolor: 'rgba(23, 75, 106, 0.02)' } }}>
                          <TableCell sx={{ py: 1.25 }}>
                            <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                              {user.display_name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ py: 1.25 }}>
                            <Chip
                              label={user.roles?.[0] || 'none'}
                              size="small"
                              sx={{
                                height: 20, fontSize: '0.65rem', fontWeight: 600,
                                bgcolor: user.roles?.[0] === 'administrator' ? 'rgba(23, 75, 106, 0.1)' : 'grey.100',
                                color: user.roles?.[0] === 'administrator' ? 'primary.main' : 'text.secondary'
                              }}
                            />
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1.25 }}>
                            <Typography variant="body2" color="text.secondary">{user.post_count}</Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Alert severity={wpUsers?.error_type === 'cloudflare' ? 'warning' : 'info'} sx={{ borderRadius: 2, fontSize: '0.8rem' }}>
                    <strong>{wpUsers?.error || 'Unable to fetch users'}</strong>
                    {wpUsers?.fix && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{wpUsers.fix}</Typography>
                    )}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* WordPress Plugins */}
        {domain.plugin_installed !== false && (
          <Grid item xs={12} md={6}>
            <Card sx={{
              borderRadius: 3, border: '1px solid', borderColor: 'grey.200',
              boxShadow: '0px 12px 18px -6px rgba(0, 0, 0, 0.08)',
              height: 400, display: 'flex', flexDirection: 'column'
            }}>
              <CardHeader
                title="WordPress Plugins"
                avatar={<PowerSettingsNew color="primary" />}
                titleTypographyProps={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontWeight: 500, fontSize: '1.3rem'
                }}
                action={
                  wpPlugins && (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Chip label={`${wpPlugins.active_count} active`} size="small"
                        sx={{ fontWeight: 600, fontSize: '0.65rem', bgcolor: 'rgba(46,125,50,0.1)', color: 'success.dark' }}
                      />
                      {wpPlugins.updates_available > 0 && (
                        <Chip label={`${wpPlugins.updates_available} updates`} size="small"
                          sx={{ fontWeight: 600, fontSize: '0.65rem', bgcolor: 'rgba(198,168,98,0.15)', color: 'warning.dark' }}
                        />
                      )}
                    </Box>
                  )
                }
              />
              <CardContent sx={{ pt: 0, flex: 1, overflow: 'auto' }}>
                {loadingWpPlugins ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} sx={{ color: 'primary.main' }} />
                  </Box>
                ) : wpPlugins?.success && wpPlugins?.plugins ? (
                  <Table size="small" sx={{ '& td, & th': { borderColor: 'grey.100' } }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1, borderBottom: '2px solid', borderColor: 'grey.200' }}>Plugin</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1, borderBottom: '2px solid', borderColor: 'grey.200' }}>Version</TableCell>
                        <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1, borderBottom: '2px solid', borderColor: 'grey.200' }} align="right">Status</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {wpPlugins.plugins.map((plugin, idx) => (
                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: 'rgba(23, 75, 106, 0.02)' } }}>
                          <TableCell sx={{ py: 1.25 }}>
                            <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.85rem' }}>
                              {plugin.name}
                            </Typography>
                            {plugin.author && (
                              <Typography variant="caption" color="text.secondary">
                                by {plugin.author}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ py: 1.25 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              {plugin.version}
                            </Typography>
                            {plugin.has_update && (
                              <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                                {plugin.new_version} available
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1.25 }}>
                            <Chip
                              label={plugin.is_active ? 'Active' : 'Inactive'}
                              size="small"
                              sx={{
                                height: 20, fontSize: '0.65rem', fontWeight: 600,
                                bgcolor: plugin.is_active ? 'rgba(46,125,50,0.1)' : 'rgba(0,0,0,0.06)',
                                color: plugin.is_active ? 'success.dark' : 'text.secondary'
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <Alert severity={wpPlugins?.error_type === 'cloudflare' ? 'warning' : 'info'} sx={{ borderRadius: 2, fontSize: '0.8rem' }}>
                    <strong>{wpPlugins?.error || 'Unable to fetch plugins'}</strong>
                    {wpPlugins?.fix && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{wpPlugins.fix}</Typography>
                    )}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Email Delivery Logs */}
        {domain.plugin_installed !== false && (
          <Grid item xs={12}>
            <Card sx={{
              borderRadius: 3, border: '1px solid', borderColor: 'grey.200',
              boxShadow: '0px 12px 18px -6px rgba(0, 0, 0, 0.08)'
            }}>
              <CardHeader
                title="Email Delivery Logs"
                subheader={emailLogs?.provider ? `via ${
                  emailLogs.provider === 'gravity_smtp' ? 'Gravity SMTP' :
                  emailLogs.provider === 'wp_mail_smtp' ? 'WP Mail SMTP' :
                  emailLogs.provider === 'wp_mail_logging' ? 'WP Mail Logging' :
                  emailLogs.provider
                }` : ''}
                avatar={<Email color="primary" />}
                titleTypographyProps={{
                  fontFamily: '"Cormorant Garamond", Georgia, serif',
                  fontWeight: 500, fontSize: '1.3rem'
                }}
                subheaderTypographyProps={{
                  fontSize: '0.75rem', fontWeight: 500
                }}
                action={
                  emailLogs?.counts ? (
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      <Chip icon={<CheckCircleOutline sx={{ fontSize: 14 }} />}
                        label={`${emailLogs.counts.sent} sent`} size="small"
                        sx={{ fontWeight: 600, fontSize: '0.65rem', bgcolor: 'rgba(46,125,50,0.1)', color: 'success.dark' }}
                      />
                      {emailLogs.counts.failed > 0 && (
                        <Chip icon={<ErrorOutline sx={{ fontSize: 14 }} />}
                          label={`${emailLogs.counts.failed} failed`} size="small"
                          sx={{ fontWeight: 600, fontSize: '0.65rem', bgcolor: 'rgba(211,47,47,0.1)', color: 'error.dark' }}
                        />
                      )}
                    </Box>
                  ) : null
                }
              />
              <CardContent sx={{ pt: 0 }}>
                {loadingEmailLogs ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} sx={{ color: 'primary.main' }} />
                  </Box>
                ) : emailLogs?.success === false ? (
                  <Alert severity={emailLogs?.error_type === 'cloudflare' ? 'warning' : 'info'} sx={{ borderRadius: 2, fontSize: '0.8rem' }}>
                    <strong>{emailLogs.error || 'No email logging plugin detected on this site'}</strong>
                    {emailLogs?.fix && (
                      <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{emailLogs.fix}</Typography>
                    )}
                  </Alert>
                ) : emailLogs?.emails ? (
                  <>
                    {/* Filters */}
                    <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                      <ToggleButtonGroup
                        value={emailFilter}
                        exclusive
                        onChange={(e, v) => v && handleEmailFilterChange(v)}
                        size="small"
                      >
                        <ToggleButton value="all" sx={{ textTransform: 'none', px: 2, fontSize: '0.75rem' }}>
                          All ({emailLogs.counts?.total || 0})
                        </ToggleButton>
                        <ToggleButton value="sent" sx={{ textTransform: 'none', px: 2, fontSize: '0.75rem' }}>
                          Sent ({emailLogs.counts?.sent || 0})
                        </ToggleButton>
                        <ToggleButton value="failed" sx={{ textTransform: 'none', px: 2, fontSize: '0.75rem', color: emailLogs.counts?.failed > 0 ? 'error.main' : 'inherit' }}>
                          Failed ({emailLogs.counts?.failed || 0})
                        </ToggleButton>
                      </ToggleButtonGroup>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          type="date"
                          size="small"
                          label="From"
                          value={emailDateFrom}
                          onChange={(e) => handleEmailDateChange(e.target.value, emailDateTo)}
                          InputLabelProps={{ shrink: true }}
                          sx={{ width: 160, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } }}
                        />
                        <TextField
                          type="date"
                          size="small"
                          label="To"
                          value={emailDateTo}
                          onChange={(e) => handleEmailDateChange(emailDateFrom, e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          sx={{ width: 160, '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 } }}
                        />
                        {(emailDateFrom || emailDateTo) && (
                          <Button
                            size="small"
                            onClick={() => handleEmailDateChange('', '')}
                            sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 'auto', color: 'text.secondary' }}
                          >
                            Clear
                          </Button>
                        )}
                      </Box>
                      <Box sx={{ flexGrow: 1 }} />
                      <Typography variant="caption" color="text.secondary">
                        {emailLogs.total} emails
                      </Typography>
                    </Box>

                    {/* Table */}
                    <Table size="small" sx={{ '& td, & th': { borderColor: 'grey.100' } }}>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1, borderBottom: '2px solid', borderColor: 'grey.200' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1, borderBottom: '2px solid', borderColor: 'grey.200' }}>Subject</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1, borderBottom: '2px solid', borderColor: 'grey.200' }}>To</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1, borderBottom: '2px solid', borderColor: 'grey.200' }}>Source</TableCell>
                          <TableCell sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', py: 1, borderBottom: '2px solid', borderColor: 'grey.200' }} align="right">Date</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {emailLogs.emails.map((email) => (
                          <TableRow key={email.id} sx={{
                            bgcolor: email.status === 'failed' ? 'rgba(211,47,47,0.04)' : 'transparent',
                            '&:hover': { bgcolor: email.status === 'failed' ? 'rgba(211,47,47,0.08)' : 'rgba(23, 75, 106, 0.02)' }
                          }}>
                            <TableCell sx={{ py: 1.25 }}>
                              {email.status === 'sent' ? (
                                <CheckCircleOutline sx={{ fontSize: 18, color: 'success.main' }} />
                              ) : email.status === 'failed' ? (
                                <ErrorOutline sx={{ fontSize: 18, color: 'error.main' }} />
                              ) : (
                                <Chip label={email.status} size="small" sx={{ height: 18, fontSize: '0.6rem' }} />
                              )}
                            </TableCell>
                            <TableCell sx={{ py: 1.25, maxWidth: 300 }}>
                              <Typography variant="body2" fontWeight={500} sx={{
                                fontSize: '0.8rem', overflow: 'hidden',
                                textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                              }}>
                                {email.subject}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.25 }}>
                              <Typography variant="body2" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
                                {email.to}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ py: 1.25 }}>
                              <Chip label={email.source || email.service || '—'} size="small"
                                sx={{ height: 18, fontSize: '0.6rem', fontWeight: 500 }}
                              />
                            </TableCell>
                            <TableCell align="right" sx={{ py: 1.25 }}>
                              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                                {email.date ? parseUTC(email.date).toLocaleString() : '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                        {emailLogs.emails.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={5} sx={{ textAlign: 'center', py: 3 }}>
                              <Typography variant="body2" color="text.secondary">No emails found</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>

                    {/* Pagination */}
                    {emailLogs.total_pages > 1 && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                        <Pagination
                          count={emailLogs.total_pages}
                          page={emailPage}
                          onChange={(e, v) => handleEmailPageChange(v)}
                          color="primary"
                          size="small"
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">Unable to fetch email logs</Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Cloudflare Fix Dialog */}
      <Dialog
        open={cfDialog}
        onClose={() => { setCfDialog(false); setShowCfInput(false); setCfToken(''); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Warning sx={{ color: 'warning.main', fontSize: 28 }} />
          Cloudflare is Blocking Nerv
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Unable to connect to <strong>{domain?.domain_name}</strong> — Cloudflare bot protection is blocking API requests from the Nerv server.
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            To fix this, you need to whitelist the Nerv server IP (<strong>35.208.152.255</strong>) in Cloudflare's firewall rules. You can do this automatically with a Cloudflare API token, or manually.
          </Typography>

          {/* Step by step guide */}
          <Box sx={{ bgcolor: 'grey.50', borderRadius: 2, p: 2, mb: 2, border: '1px solid', borderColor: 'grey.200' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              How to get a Cloudflare API Token:
            </Typography>
            <Box component="ol" sx={{ pl: 2.5, m: 0, '& li': { mb: 0.75 } }}>
              <Typography component="li" variant="body2" color="text.secondary">
                Go to <strong>dash.cloudflare.com</strong> and log in
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Click your profile icon (top right) → <strong>My Profile</strong>
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Select <strong>API Tokens</strong> tab → <strong>Create Token</strong>
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Use <strong>"Create Custom Token"</strong> template
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Add permission: <strong>Zone → Firewall Services → Edit</strong>
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Zone Resources: <strong>Include → Specific zone</strong> → select <strong>{domain?.domain_name}</strong>
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Click <strong>Continue to summary → Create Token</strong>
              </Typography>
              <Typography component="li" variant="body2" color="text.secondary">
                Copy the token and paste it below
              </Typography>
            </Box>
          </Box>

          {!showCfInput ? (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<Shield />}
                onClick={() => setShowCfInput(true)}
              >
                {domain?.cloudflare_api_token_masked ? 'Fix with Saved Token' : 'Enter Cloudflare API Token'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.open('https://dash.cloudflare.com/', '_blank')}
              >
                Open Cloudflare
              </Button>
            </Box>
          ) : (
            <Box>
              {!domain?.cloudflare_api_token_masked && (
                <TextField
                  size="small"
                  fullWidth
                  label="Cloudflare API Token"
                  value={cfToken}
                  onChange={(e) => setCfToken(e.target.value)}
                  placeholder="Paste your Cloudflare API token"
                  type="password"
                  sx={{ mb: 2 }}
                  helperText="Token needs Zone → Firewall Services → Edit permission"
                />
              )}
              {domain?.cloudflare_api_token_masked && (
                <Alert severity="info" sx={{ mb: 2, fontSize: '0.85rem' }}>
                  Using saved token: <strong>{domain.cloudflare_api_token_masked}</strong>
                </Alert>
              )}
              <Button
                variant="contained"
                color="primary"
                startIcon={cfFixing ? <CircularProgress size={16} color="inherit" /> : <Shield />}
                disabled={cfFixing || (!domain?.cloudflare_api_token_masked && !cfToken.trim())}
                onClick={async () => {
                  setCfFixing(true);
                  try {
                    const payload = cfToken.trim() ? { cloudflare_api_token: cfToken.trim() } : {};
                    const res = await api.post(
                      `/sites/${domain.site_id}/domains/${domain.id}/cloudflare-whitelist`,
                      payload
                    );
                    setSnackbar({ open: true, message: res.data.message || 'Server IP whitelisted successfully', severity: 'success' });
                    setCfDialog(false);
                    setShowCfInput(false);
                    setCfToken('');
                    // Re-check health after delay
                    setTimeout(() => {
                      fetchWpHealth(domain.site_id, domain.id);
                      fetchWpUsers(domain.site_id, domain.id);
                      fetchWpPlugins(domain.site_id, domain.id);
                    }, 3000);
                  } catch (err) {
                    setSnackbar({ open: true, message: err.response?.data?.detail || 'Cloudflare API error', severity: 'error' });
                  } finally {
                    setCfFixing(false);
                  }
                }}
              >
                {cfFixing ? 'Whitelisting...' : 'Whitelist Server IP'}
              </Button>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCfDialog(false); setShowCfInput(false); setCfToken(''); }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
      )}

      {activeTab === 1 && (
        <Box>
          <RestoreProgressPanel status={restoreStatus} />
          <BackupList
            backups={backups}
            onRestore={(b) => setRestoreDialog({ open: true, backup: b })}
            onRunNow={handleRunBackupNow}
            onDownload={handleDownloadBackup}
            enabled={domain?.host_provider === 'nerv'}
          />
        </Box>
      )}

      {activeTab === 2 && (
        <Box>
          <HostInfoPanel data={hostInfo} />
        </Box>
      )}

      <RestoreConfirmDialog
        open={restoreDialog.open}
        backup={restoreDialog.backup}
        onConfirm={handleRestoreConfirmed}
        onCancel={() => setRestoreDialog({ open: false, backup: null })}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default DomainDetail;
